"use client"

import MuiTab from "@mui/material/Tab"
import MuiTabs from "@mui/material/Tabs"
import type { TabScrollButtonProps } from "@mui/material/TabScrollButton"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import { FreeMode } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import GradientChevron from "../../icons/GradientChevron"
import {
  CategoryScrollButton,
  ChevronWrapper,
  CounterText,
  DesktopCaption,
  DesktopOnlyLayer,
  HeaderSection,
  ImageCounterBadge,
  MainImageContainer,
  MainImageGroup,
  MainImageOverlay,
  MainVideo,
  MobileCaption,
  MobileOnlyLayer,
  NavArrowButton,
  NavArrowsContainer,
  Root,
  Subtitle,
  ThumbnailImage,
  ThumbnailItem,
  ThumbnailStripWrapper,
  ThumbnailSwiperContainer,
  ThumbnailVideo,
  Title,
  TitleRow,
  VideoPlayOverlay,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ImageGalleryData = components["schemas"]["ImageGalleryPropertiesModel"]
type GalleryCategoryData =
  components["schemas"]["GalleryCategoryItemPropertiesModel"]
// EGDE-718: `description` is the gallery item's "Image Title" field — the line
// the design lays over the bottom of the image on display. It was lost to CMS
// drift once (see the tolerant typing this replaces) and is back on the element
// type, so the generated model carries it again.
type GalleryImageData = components["schemas"]["GalleryImageItemPropertiesModel"]

interface Props {
  data: ImageGalleryData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ALL_KEY = "__all__"

// ---------------------------------------------------------------------------
// Custom Tab Scroll Button — scrolls by one tab per click (ESEIM-201)
// ---------------------------------------------------------------------------
function CustomTabScrollButton({
  direction,
  disabled,
  onClick,
}: TabScrollButtonProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget
    const tabsRoot = button.closest(".MuiTabs-root")
    const scroller = tabsRoot?.querySelector(
      ".MuiTabs-scroller",
    ) as HTMLElement | null
    const flexContainer = scroller?.querySelector(
      ".MuiTabs-flexContainer",
    ) as HTMLElement | null

    if (!scroller || !flexContainer) {
      onClick?.(e)
      return
    }

    const tabs = Array.from(
      flexContainer.querySelectorAll(".MuiTab-root"),
    ) as HTMLElement[]
    const scrollerRect = scroller.getBoundingClientRect()

    if (direction === "left") {
      const hiddenLeft = tabs
        .filter((tab) => tab.getBoundingClientRect().left < scrollerRect.left - 1)
        .pop()
      if (hiddenLeft) {
        const offset = hiddenLeft.offsetLeft - scroller.offsetLeft
        scroller.scrollTo({ left: offset, behavior: "smooth" })
      }
    } else {
      const hiddenRight = tabs.find(
        (tab) => tab.getBoundingClientRect().right > scrollerRect.right + 1,
      )
      if (hiddenRight) {
        const offset =
          hiddenRight.offsetLeft -
          scroller.offsetLeft -
          scroller.clientWidth +
          hiddenRight.offsetWidth
        scroller.scrollTo({ left: offset, behavior: "smooth" })
      }
    }
  }

  if (disabled) return null

  return (
    <CategoryScrollButton
      onClick={handleClick}
      aria-label={direction === "left" ? "Scroll tabs back" : "Scroll tabs forward"}
    >
      {direction === "left" ? <ChevronLeft /> : <ChevronRight />}
    </CategoryScrollButton>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isVideoMedia(mediaPicker: unknown): boolean {
  if (!Array.isArray(mediaPicker) || mediaPicker.length === 0) return false
  const media = mediaPicker[0] as Record<string, unknown>
  const mediaType = (String(media?.mediaType ?? "")).toLowerCase()
  const extension = (String(media?.extension ?? "")).toLowerCase()
  // EGDE-566/571: Umbraco reports "umbracoMediaVideo" (with empty extension)
  // for uploaded videos — strict equality never matched (cf. EGDE-36).
  return (
    mediaType.includes("video") ||
    ["mp4", "webm", "ogg", "mov"].includes(extension)
  )
}

function getResponsiveImageUrl(blockList: unknown) {
  const list = blockList as
    | { items?: Array<{ content: { properties?: Record<string, unknown> } }> }
    | undefined
  const item = list?.items?.[0]
  const props = item?.content?.properties
  if (!props) return { desktop: null, mobile: null, alt: "", isVideo: false }

  const desktopRel = getMediaUrl(props.desktopImage as unknown)
  const mobileRel = getMediaUrl(props.mobileImage as unknown)
  const isVideo =
    isVideoMedia(props.desktopImage) || isVideoMedia(props.mobileImage)

  return {
    desktop: desktopRel ? getUmbracoMediaUrl(desktopRel) : null,
    mobile: mobileRel
      ? getUmbracoMediaUrl(mobileRel)
      : desktopRel
        ? getUmbracoMediaUrl(desktopRel)
        : null,
    alt: (props.altText as string) ?? "",
    isVideo,
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function ImageGallery({ data, culture }: Props) {
  const [activeCategory, setActiveCategory] = useState(ALL_KEY)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const thumbnailSwiperRef = useRef<SwiperType | null>(null)

  const isRtl = culture === "he-IL" || culture === "ar"

  // Parse categories from CMS — collect all first, then filter below
  const categoryItems = data.categories?.items ?? []
  const allCategories: Array<{ key: string; label: string }> =
    categoryItems.map((block) => {
      const props = block.content.properties as GalleryCategoryData | undefined
      return {
        key: props?.categoryKey ?? "",
        label: props?.label ?? "",
      }
    })

  // Parse gallery items from CMS
  const allGalleryItems = data.galleryItems?.items ?? []
  const galleryImages = allGalleryItems
    .filter((block) => {
      const props = block.content.properties as GalleryImageData | undefined
      // Hide only when the visibility toggle is explicitly false.
      // Umbraco returns null/undefined for an untouched toggle, which must
      // still be treated as visible. (EGDE-70: a single "Is Visible" toggle.)
      return props?.isVisible !== false
    })
    .map((block, index) => {
      const props = block.content.properties as GalleryImageData | undefined
      const imageData = getResponsiveImageUrl(props?.image)
      return {
        id: block.content.id ?? `gallery-${index}`,
        desktop: imageData.desktop,
        mobile: imageData.mobile,
        alt: imageData.alt,
        description: props?.description ?? "",
        isVideo: imageData.isVideo,
        categoryKey: props?.categoryKey ?? "",
      }
    })

  // Only show categories that have at least one gallery item (ESEIM-196)
  const categories = allCategories.filter((cat) =>
    galleryImages.some((img) => img.categoryKey === cat.key),
  )

  // Filter images by active category
  const filteredImages =
    activeCategory === ALL_KEY
      ? galleryImages
      : galleryImages.filter((img) => img.categoryKey === activeCategory)

  // Current main image
  const currentImage = filteredImages[activeImageIndex] ?? filteredImages[0]

  // "All" tab label based on culture
  const allLabel = culture === "he-IL" ? "\u05d4\u05db\u05dc" : "All"

  // Handle category change
  const handleCategoryChange = useCallback((_: unknown, value: string) => {
    setActiveCategory(value)
    setActiveImageIndex(0)
  }, [])

  // Handle thumbnail click
  const handleThumbnailClick = useCallback((index: number) => {
    setActiveImageIndex(index)
  }, [])

  // Handle main image arrow navigation
  const handlePrevImage = useCallback(() => {
    setActiveImageIndex((prev) => Math.max(0, prev - 1))
  }, [])

  const handleNextImage = useCallback(() => {
    setActiveImageIndex((prev) => Math.min(filteredImages.length - 1, prev + 1))
  }, [filteredImages.length])

  const prevDisabled = activeImageIndex <= 0
  const nextDisabled = activeImageIndex >= filteredImages.length - 1

  // Keep the swiper in sync when filtered images change (e.g., category switch).
  // EGDE-335: the thumbnail strip no longer shows scroll arrows (Figma keeps the
  // arrows on the main image only), so the overflow detection was removed — the
  // strip still scrolls by drag/swipe via FreeMode.
  useEffect(() => {
    const swiper = thumbnailSwiperRef.current
    if (swiper) {
      swiper.update()
    }
  }, [filteredImages.length])

  // Scroll thumbnail swiper to active image
  useEffect(() => {
    const swiper = thumbnailSwiperRef.current
    if (swiper && activeImageIndex >= 0) {
      swiper.slideTo(activeImageIndex)
    }
  }, [activeImageIndex])

  const imageCounter = `${activeImageIndex + 1}/${filteredImages.length}`

  return (
    <Container paddingMobile>
      <Root>
        {/* Header: title + subtitle */}
        <HeaderSection>
          <TitleRow>
            <ChevronWrapper>
              <GradientChevron width={17} height={27} />
            </ChevronWrapper>
            <Title variant="h2">{data.title}</Title>
          </TitleRow>
          {data.subtitle && (
            <Subtitle variant="body1">{data.subtitle}</Subtitle>
          )}
        </HeaderSection>

        {/* Category tabs — MUI Tabs with scrollable variant */}
        <MuiTabs
          value={activeCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          ScrollButtonComponent={CustomTabScrollButton}
        >
          <MuiTab label={allLabel} value={ALL_KEY} />
          {categories.map((cat) => (
            <MuiTab key={cat.key} label={cat.label} value={cat.key} />
          ))}
        </MuiTabs>

        {/* Main featured image or video */}
        {currentImage && (
          <MainImageGroup>
            <MainImageContainer>
              {currentImage.desktop &&
                (currentImage.isVideo ? (
                  <MainVideo
                    key={currentImage.id}
                    src={currentImage.desktop}
                    controls
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  // EGDE-602: the block read only the desktop asset, so an
                  // authored Mobile Image never appeared. `mobile` already falls
                  // back to the desktop asset when no mobile crop is set, per the
                  // CMS field description. `sizes` is set so the hidden layer is
                  // not fetched at either viewport.
                  <>
                    <DesktopOnlyLayer>
                      <Image
                        src={currentImage.desktop}
                        alt={currentImage.alt || currentImage.description}
                        fill
                        sizes="(min-width: 1280px) 1200px, 1px"
                        style={{ objectFit: "cover" }}
                        priority
                      />
                    </DesktopOnlyLayer>
                    <MobileOnlyLayer>
                      <Image
                        src={currentImage.mobile ?? currentImage.desktop}
                        alt={currentImage.alt || currentImage.description}
                        fill
                        sizes="(min-width: 1280px) 1px, 100vw"
                        style={{ objectFit: "cover" }}
                        priority
                      />
                    </MobileOnlyLayer>
                  </>
                ))}
              {!currentImage.isVideo && <MainImageOverlay />}
              <ImageCounterBadge>
                <CounterText variant="body1">{imageCounter}</CounterText>
              </ImageCounterBadge>
              {currentImage.description && (
                <DesktopCaption variant="body1">
                  {currentImage.description}
                </DesktopCaption>
              )}
              <NavArrowsContainer>
                <NavArrowButton
                  onClick={handlePrevImage}
                  disabled={prevDisabled}
                  aria-label="Previous"
                >
                  {/* NavArrowsContainer is flex + justify-content:space-between, so
                      the browser reverses the two buttons' visual order under dir=rtl
                      (the @noflip only pins the box's left/right edges). Swap the
                      glyph by direction so the on-screen chevron always points the
                      right way: prev button = left-pointing in LTR, right-pointing
                      (lands on the visual right) in RTL. */}
                  {isRtl ? <ChevronRight /> : <ChevronLeft />}
                </NavArrowButton>
                <NavArrowButton
                  onClick={handleNextImage}
                  disabled={nextDisabled}
                  aria-label="Next"
                >
                  {isRtl ? <ChevronLeft /> : <ChevronRight />}
                </NavArrowButton>
              </NavArrowsContainer>
            </MainImageContainer>
            {currentImage.description && (
              <MobileCaption variant="body1">
                {currentImage.description}
              </MobileCaption>
            )}
          </MainImageGroup>
        )}

        <ThumbnailStripWrapper>
          <ThumbnailSwiperContainer>
            <Swiper
              dir={isRtl ? "rtl" : "ltr"}
              modules={[FreeMode]}
              slidesPerView="auto"
              spaceBetween={12}
              freeMode={{ enabled: true, momentumRatio: 0.5 }}
              touchEventsTarget="container"
              onSwiper={(s) => {
                thumbnailSwiperRef.current = s
              }}
              breakpoints={{
                1280: { spaceBetween: 24 },
              }}
            >
              {filteredImages.map((img, index) => (
                <SwiperSlide key={img.id} style={{ width: "auto" }}>
                  <ThumbnailItem
                    $selected={activeImageIndex === index}
                    onClick={() => handleThumbnailClick(index)}
                    aria-label={img.alt || `Gallery image ${index + 1}`}
                  >
                    {img.desktop &&
                      (img.isVideo ? (
                        <ThumbnailImage>
                          <ThumbnailVideo
                            src={`${img.desktop}#t=0.1`}
                            muted
                            playsInline
                            preload="metadata"
                          />
                          <VideoPlayOverlay aria-hidden="true">
                            &#9654;
                          </VideoPlayOverlay>
                        </ThumbnailImage>
                      ) : (
                        // EGDE-602 (r2): the thumbnail follows the main image onto
                        // the Mobile Image. Shipping it on the desktop asset left
                        // the strip showing a different crop than the image above
                        // it, which is what QA returned the ticket for.
                        <ThumbnailImage>
                          <DesktopOnlyLayer>
                            <Image
                              src={img.desktop}
                              alt={img.alt || ""}
                              fill
                              sizes="(min-width: 1280px) 289px, 1px"
                              style={{ objectFit: "cover" }}
                            />
                          </DesktopOnlyLayer>
                          <MobileOnlyLayer>
                            <Image
                              src={img.mobile ?? img.desktop}
                              alt={img.alt || ""}
                              fill
                              sizes="(min-width: 1280px) 1px, 142px"
                              style={{ objectFit: "cover" }}
                            />
                          </MobileOnlyLayer>
                        </ThumbnailImage>
                      ))}
                  </ThumbnailItem>
                </SwiperSlide>
              ))}
            </Swiper>
          </ThumbnailSwiperContainer>
        </ThumbnailStripWrapper>
      </Root>
    </Container>
  )
}
